import type { DocumentNode } from 'graphql';
import {
  pricingPageV3Query_plans,
  pricingPageV3Query_plans_plans,
  pricingPageV3Query_plans_plans_features,
} from '@zapier/design-system-revenue/src/hooks/usePlans/types/pricingPageV3Query';
import { invoices_account_invoices } from '@zapier/design-system-revenue/src/hooks/usePendingPayment/types/invoices';
import { currencyRulesetQuery_account_currencyRuleset } from '@zapier/design-system-revenue/src/hooks/useCurrencyRuleset/types/currencyRulesetQuery';
import { invoicePreviewQuery_account_invoicePreview } from '@zapier/design-system-revenue/src/hooks/useInvoicePreview/types/invoicePreviewQuery';

export type CurrencyRulesetItem = currencyRulesetQuery_account_currencyRuleset;
export type PendingInvoice = invoices_account_invoices;
export type PlanV2 = pricingPageV3Query_plans_plans;
export type Invoice = invoicePreviewQuery_account_invoicePreview;

export type Size = 'large' | 'medium' | 'small';

export type TooltipPosition =
  | 'northwest'
  | 'north'
  | 'northeast'
  | 'east'
  | 'southeast'
  | 'south'
  | 'southwest'
  | 'west';

export type TrialType = 'Professional' | 'Team' | 'Company';
export type PlanType = 'Free' | 'Starter' | TrialType;
export type ChangeType = 'change' | 'downgrade' | 'upgrade';
export type Interval = 'month' | 'year';

export type PlanIntervalProps = {
  __typename?: 'PlanIntervalProps';
  amount: number;
  id: number;
  interval: Interval;
  monthlyAmount: number;
};

export type TaskLimitVariant = {
  __typename?: 'TaskLimitVariant';
  intervals: Array<PlanIntervalProps>;
  name: string;
  tasks: number;
};

export type PlanLimits = {
  premium_apps_limit: number;
  zap_limit: number;
  action_limit: number;
};

export type AccountPlanTier = [number, number];

export type AccountPlan = {
  __typename?: string;
  actions: number;
  allow_branching_zaps: boolean;
  allow_companies: boolean;
  allow_premium_apps: boolean;
  allow_teams: boolean;
  apps: number;
  authentications: number;
  can_autoreplay_errored_tasks: boolean;
  intervals: Array<PlanIntervalProps>;
  limits: PlanLimits;
  max_metering_charge: number;
  meta: Array<any>;
  metered_pricing_tiers?: Array<AccountPlanTier>;
  metered_task_pricing: boolean;
  min_seats: number;
  name: string;
  off_plan_group?: boolean;
  per_extra_action?: number;
  plan_type: PlanType;
  polling_interval_minutes: number;
  trial_period_days?: number;
  task_limit_variants: Array<TaskLimitVariant>;
  type: string;
  zap_length: number;
  zaps: number;
};

export type FeatureListing = pricingPageV3Query_plans_plans_features;

export type Feature = {
  hasFeature: (plan: PlanV2) => boolean;
  helpText: string;
  helpUrl: string;
  key: string;
  name: string;
};

export type Features = {
  hasAutoReplay?: boolean;
  hasPaths?: boolean;
  hasDeveloperApps?: boolean;
  hasMeteredBilling?: boolean;
  hasMultiStepZaps?: boolean;
  hasPremiumApps?: boolean;
  hasTeams?: boolean;
};

export type PlanTier = {
  numTasks: number;
  pricePerTask: number;
};

export type QuestionProps = {
  isChecked?: boolean;
  question: string;
};

export type Plan = Features & {
  __typename?: 'Plan';
  allowCompanies: boolean;
  amount: number;
  billingAmount: number;
  billingInterval: Interval;
  changePlanUrl?: string;
  description?: string;
  descriptiveName: string;
  displayName: string;
  features?: Array<FeatureListing>;
  featuresTag?: string;
  hasTeams?: boolean;
  id: number;
  image?: string;
  intervals: Array<PlanIntervalProps>;
  isCurrentPlan?: boolean;
  isInPlanGroup: boolean;
  metered_pricing_tiers?: Array<any>;
  name: string;
  selectedTaskLimitVariant: TaskLimitVariant;
  shortName: string;
  taskLimitVariants: Array<TaskLimitVariant>;
  tasks: number;
  tiers: Array<PlanTier>;
  type: PlanType;
  zaps: number;
  zapsRunInterval: number;
};

export type PlanStack = pricingPageV3Query_plans;

export const trialExtensionReasons: {
  [x: string]: string;
} = {
  paths: 'paths',
  multistep: 'multistep',
  premium: 'premium',
  premiumLimit: 'premium-limit',
  zapLimit: 'live-zaps',
  taskLimit: 'tasks',
  taskLimit80Percent: 'tasks-80-percent',
  emailSupport: 'email-support',
  unknown: 'unknown',
};

export type TrialExtensionReason =
  | 'paths'
  | 'multistep'
  | 'premium'
  | 'premium-limit'
  | 'live-zaps'
  | 'tasks'
  | 'tasks-80-percent'
  | 'email-support'
  | 'unknown';

export const flagNames = Object.freeze({
  Australia: 'Australia',
  Brazil: 'Brazil',
  Canada: 'Canada',
  EuropeanUnion: 'EuropeanUnion',
  GreatBritain: 'GreatBritain',
  India: 'India',
  Japan: 'Japan',
  NewZealand: 'NewZealand',
  UnitedStates: 'UnitedStates',
});

export type FlagName =
  | 'Australia'
  | 'Brazil'
  | 'Canada'
  | 'EuropeanUnion'
  | 'GreatBritain'
  | 'India'
  | 'Japan'
  | 'NewZealand'
  | 'UnitedStates';

// Stubs: Remove these once we're no longer dependent on flowgen
export type FaqQuestionType = {
  answer: React.ReactNode;
  id?: string;
  question: React.ReactNode;
};

export type Enticements = {
  year: string;
  month: string;
};

export enum AppStatus {
  UPCOMING = 'upcoming',
  PREMIUM = 'premium',
  BETA = 'beta',
  FEATURED = 'featured',
}

// Loosely typed types to support dynamic `useQuery`.
export type OperationVariables = Record<string, any>;

export type QueryResult<Data = any> = {
  client: any;
  data: Data;
  error?: any;
  loading: boolean;
  networkStatus: any;
  called: boolean;
};

type TData = any;

export type UseQuery<TData = any> = (
  query: DocumentNode,
  opts: {}
) => QueryResult<TData>;

type MutationOptions = {};

type FetchResult<TData = {}> = {
  data?: TData | null;
  extensions?: any;
  context?: any;
};

export type MutationResult<TData> = Partial<QueryResult<TData>>;

type MutateFunction<TData> = (
  opts: MutationOptions
) => Promise<FetchResult<TData>>;

export type UseMutation<TData = any> = (
  query: DocumentNode,
  opts: MutationOptions
) => [MutateFunction<TData>, MutationResult<TData>];

export type ApolloInjection = {
  useMutation?: UseMutation<TData>;
  useQuery?: UseQuery<TData>;
};
